import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, ArrowRight, Check, Download } from "lucide-react";
import { toast } from "sonner";
import InfoTooltip from "./admin/InfoTooltip";
import { trackEvent, EVENTS } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Brochure download modal — lead capture form for service brochure downloads.
 * Submits to /api/brochure-request, then shows success state with link to view brochure online.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   offer: { slug, title } — the priority offer being requested
 */
const BrochureModal = ({ open, onClose, offer }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organisation: "",
    role: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setForm({ full_name: "", email: "", organisation: "", role: "", consent: false });
      setDone(false);
    }
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open || !offer) return null;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent before downloading.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/brochure-request`, {
        ...form,
        offer_slug: offer.slug,
        offer_title: offer.title,
      });
      setDone(true);
      trackEvent(EVENTS.BROCHURE_DOWNLOAD, { brochure: offer?.slug || offer?.title || "brochure" });
      toast.success("Brochure access granted — check your inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="brochure-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white max-w-xl w-full border-t-4 border-[#7737F6] shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-black p-2"
          aria-label="Close"
          data-testid="brochure-modal-close"
        >
          <X size={20} />
        </button>

        <div className="p-8 md:p-10">
          {done ? (
            <div className="text-center py-4" data-testid="brochure-success">
              <div className="w-14 h-14 mx-auto bg-[#7737F6] text-white flex items-center justify-center mb-6">
                <Check size={28} strokeWidth={2} />
              </div>
              <h3 className="ek-heading text-2xl md:text-3xl text-black">Brochure on its way.</h3>
              <p className="ek-body mt-4">
                We've sent the <strong>{offer.title}</strong> brochure to your inbox. You can also view it online now.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`/brochure/${offer.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ek-btn ek-btn-primary"
                  data-testid="brochure-view-online"
                >
                  View brochure online <ArrowRight size={16} />
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="ek-btn ek-btn-outline-dark"
                  data-testid="brochure-close-after-success"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#7737F6] mb-2">
                <Download size={14} />
                <span className="ek-eyebrow !text-[11px]">Service Brochure</span>
              </div>
              <h3 className="ek-heading text-2xl md:text-[26px] text-black leading-tight" data-testid="brochure-modal-title">
                {offer.title}
              </h3>
              <p className="ek-body text-[15px] mt-3">
                Tell us a little about yourself and we'll send you the brochure plus a short structured one-pager you can share internally.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="brochure-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="b_full_name" className="ek-label">Full name</label>
                    <input
                      id="b_full_name"
                      name="full_name"
                      type="text"
                      required
                      value={form.full_name}
                      onChange={onChange}
                      className="ek-input"
                      data-testid="brochure-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="b_email" className="ek-label">Work email
                      <InfoTooltip testid="brochure-email-info">
                        We send the brochure and a shareable one-pager to this address. A work email helps us confirm you're a genuine business enquiry — we never sell or share it.
                      </InfoTooltip>
                    </label>
                    <input
                      id="b_email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      className="ek-input"
                      data-testid="brochure-input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="b_organisation" className="ek-label">Organisation</label>
                    <input
                      id="b_organisation"
                      name="organisation"
                      type="text"
                      required
                      value={form.organisation}
                      onChange={onChange}
                      className="ek-input"
                      data-testid="brochure-input-organisation"
                    />
                  </div>
                  <div>
                    <label htmlFor="b_role" className="ek-label">Role
                      <InfoTooltip testid="brochure-role-info">
                        Your role lets us pitch the follow-up at the right altitude — board-level summary vs. hands-on delivery detail. A job title is fine (e.g. "COO", "Head of Transformation").
                      </InfoTooltip>
                    </label>
                    <input
                      id="b_role"
                      name="role"
                      type="text"
                      required
                      value={form.role}
                      onChange={onChange}
                      className="ek-input"
                      data-testid="brochure-input-role"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={onChange}
                    required
                    className="mt-1 w-4 h-4 accent-[#7737F6]"
                    data-testid="brochure-input-consent"
                  />
                  <span className="text-[13px] text-zinc-700 font-light leading-relaxed">
                    I agree to Ekselens Consulting contacting me about this brochure. See our <a href="/privacy" target="_blank" rel="noreferrer" className="underline">privacy policy</a>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="ek-btn ek-btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="brochure-submit-button"
                >
                  {submitting ? "Sending…" : (
                    <>
                      Send me the brochure <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrochureModal;
